import Debug from 'debug';
import { EventEmitter } from 'events';

type ModalStatus = 'open' | 'closing' | 'closed' | 'error';

const OFFICE_DIALOG_CLOSED = 12006;

/**
 * A model of a modal for when you want to create model modals.
 */
export class Modal extends EventEmitter {
  debug = Debug('o365:models:Modal');
  name: string;
  options: Office.DialogOptions;
  url: string;
  status: ModalStatus = 'closed';
  result: Office.AsyncResult<Office.Dialog> | null = null;

  constructor(name: string, url: string, options: Office.DialogOptions) {
    super();
    this.name = name;
    this.url = url;
    this.options = options;
  }

  /**
   * Handler for dialog closed and/or we are closing the dialog.
   */
  close = () => {
    const prefix = `close()[${this.name}]`;
    // on first close call, we will set the status to closing so any
    // other calls in the event loop should get kicked out while we
    // handle things
    if (['closed', 'closing'].includes(this.status)) {
      this.debug(`${prefix} already closed/closing`, this.status);
      return;
    }

    this.status = 'closing';
    this.debug(prefix);
    try {
      this.result?.value?.close();
      this.result = null;
    } catch (err) {
      console.error('Could not close modal', err);
    }
    this.status = 'closed';
    this.emit('closed');
    this.debug(`${prefix} done`);
  };

  closeDialog = () => {
    if (this.result) {
      this.result.value.close();
    }
    this.close();
  };

  /**
   * Clean up the modal, we're done with it...
   */
  destroy = () => {
    this.debug(`destroy()[${this.name}]`);
    this.removeAllListeners();
    // make sure we have closed the dialog if we are destroying it...
    // all event listeners are removed so the impending 'closed' event
    // won't cause any problems....
    if (!['closing', 'closed'].includes(this.status)) {
      this.close();
    }
  };

  onDialogEventReceived = (event: { error: number } | { message: string }) => {
    this.debug('onDialogEventReceived', event);
    this.emit('event', event);
    if ('error' in event && typeof event.error !== 'undefined' && event.error === OFFICE_DIALOG_CLOSED) {
      this.close();
    }
  };

  onMessageReceived = (message: { message: string; origin?: string } | { error: number }) => {
    if ('error' in message) {
      return;
    }
    try {
      const data = JSON.parse(message.message);
      this.emit('message', data);
    } catch (err) {
      console.error(message, err);
    }
  };

  /**
   * Open the modal
   */
  async open(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        Office.context.ui.displayDialogAsync(
          this.url,
          // Office mutates this object which will cause errors if you try to
          // try to open a new modal because it will have the callback in the
          // the options :facepalm:
          { ...this.options },
          (result) => {
            this.debug('open result', result);
            this.result = result;
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              this.status = 'open';
              result.value.addEventHandler(Office.EventType.DialogMessageReceived, this.onMessageReceived);
              result.value.addEventHandler(Office.EventType.DialogEventReceived, this.onDialogEventReceived);
              this.emit('open', result);
            } else {
              this.status = 'error';
              this.emit('error', result);
            }
            resolve();
          },
        );
      } catch (err) {
        this.result = null;
        this.debug('Error opening dialog', err);
        this.emit('error', err);
        reject(err);
      }
    });
  }
}
